import { useState, useEffect, FC, useRef, useCallback } from "react";
import {
  Heading,
  Flex,
  Box,
  Text,
  Image,
  Icon,
  Button,
  useDisclosure,
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { PiMinusCircleFill } from "react-icons/pi";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

import { getProviderServices, getServiceExtras } from "../../services/profile";
import { ServiceList } from "../../components/ServiceList";
import { getCookie, formatDurationHM } from "../../core/utils";
import { SERVICES_IMAGE_URL } from "../../core/constants";
import EmptyService from "../../assets/images/emptyService.png";

interface ServiceProps {
  id: string;
  cart: any[];
  setCart: any;
  drawerDisclosure: any;
  slug: string | undefined;
  name?: string | undefined;
}

export const Services: FC<ServiceProps> = ({
  id,
  cart,
  setCart,
  drawerDisclosure,
  slug,
  name,
}) => {
  const navigate = useNavigate();

  const [activeCategory, setActiveCategory] = useState<string>("Braids");
  const [services, setServices] = useState<any[]>([]);
  const [extras, setExtras] = useState<any[]>([]);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const updateScrollButtons = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  const scrollByAmount = (direction: "left" | "right") => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Smart scroll: scroll by ~80% of visible width for smooth navigation
    const scrollAmount = container.clientWidth * 0.8;
    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    updateScrollButtons();
    container.addEventListener("scroll", updateScrollButtons);
    window.addEventListener("resize", updateScrollButtons);

    return () => {
      container.removeEventListener("scroll", updateScrollButtons);
      window.removeEventListener("resize", updateScrollButtons);
    };
  }, [updateScrollButtons, services]);

  useEffect(() => {
    const fetchServices = async () => {
      setServices(await getProviderServices(id));
      setExtras(await getServiceExtras());
    };

    fetchServices();
  }, [id]);

  useEffect(() => {
    if (services && services?.length !== 0) {
      setActiveCategory(services[0]._id);
    }
  }, [services]);

  const modalDisclosure = useDisclosure();

  const jwt = getCookie("book-d booking token");

  const handleSubmit = async () => {
    if (jwt)
      navigate("/schedule", {
        state: { name, cart },
      });
    else
      navigate("/login", {
        state: { cart },
      });
  };

  return (
    <Box>
      <Heading
        fontSize={"24px"}
        color={"#19202D"}
        mt="40px"
        display={{ base: "none", md: "flex" }}
      >
        Services
      </Heading>

      <Flex mt="20px" align="center" position="relative">
        {canScrollLeft && (
          <Flex
            position="absolute"
            left="0"
            zIndex={1}
            h="100%"
            align="center"
            bg="linear-gradient(to right, white 60%, transparent)"
            pr="20px"
            cursor="pointer"
            onClick={() => scrollByAmount("left")}
          >
            <Icon as={FiChevronLeft} boxSize="24px" color="#9BA3A3" />
          </Flex>
        )}

        <Flex
          ref={scrollContainerRef}
          flex={1}
          gap={"10px"}
          overflowX={"scroll"}
          whiteSpace={"nowrap"}
          css={{
            "&::-webkit-scrollbar": { display: "none" },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          {services?.length !== 0 &&
            services?.map((subCategory: any) => (
              <Box
                key={subCategory._id}
                minWidth={"max-content"}
                onClick={() => setActiveCategory(subCategory._id)}
                cursor={"pointer"}
              >
                <Box px="10px" mb="6px">
                  <Text
                    fontSize={"16px"}
                    color={
                      activeCategory === subCategory._id ? "#FF8E3C" : "#9BA3A3"
                    }
                    fontWeight={600}
                  >
                    {subCategory.sub_category_name} (
                    {subCategory.services.length})
                  </Text>
                </Box>

                <Box
                  h="3px"
                  bg={
                    activeCategory === subCategory._id ? "#FF8E3C" : "#E9EDEF"
                  }
                  borderRadius={"10px"}
                />
              </Box>
            ))}
        </Flex>

        {canScrollRight && (
          <Flex
            position="absolute"
            right="0"
            zIndex={1}
            h="100%"
            align="center"
            bg="linear-gradient(to left, white 60%, transparent)"
            pl="20px"
            cursor="pointer"
            onClick={() => scrollByAmount("right")}
          >
            <Icon as={FiChevronRight} boxSize="24px" color="#9BA3A3" />
          </Flex>
        )}
      </Flex>

      {services
        ?.filter((service: any) => service._id === activeCategory)
        .map((service: any) => (
          <ServiceList
            service={service}
            key={service._id}
            modalDisclosure={modalDisclosure}
            drawerDisclosure={drawerDisclosure}
            business_id={id}
            extras={extras}
            cart={cart}
            setCart={setCart}
          />
        ))}

      <Drawer
        placement={"bottom"}
        onClose={drawerDisclosure.onClose}
        isOpen={drawerDisclosure.isOpen}
        blockScrollOnMount={false}
      >
        <DrawerOverlay />

        <DrawerContent
          borderTopLeftRadius={"20px"}
          borderTopRightRadius={"20px"}
          h={{ base: "60vh", md: "75vh" }}
        >
          <DrawerCloseButton />

          <DrawerHeader pt="32px">
            <Heading fontSize={"18px"} color={"#4F4F51"}>
              Selected Services
            </Heading>
          </DrawerHeader>

          <DrawerBody>
            {cart.length !== 0 &&
              cart.map((item, index) => {
                // The duration the selected length/size actually resolved to,
                // already computed when the item was added to the cart.
                const totalDurationMin = Number(
                  item.finalDuration ?? item.service.duration_minute ?? 0
                );

                return (
                  <Box key={index} mb="32px">
                    <Flex gap="10px" mb="10px">
                      <Image
                        src={`${SERVICES_IMAGE_URL}/${item.service.serviceImage?.file_name}`}
                        fallbackSrc={EmptyService}
                        alt="Service image"
                        objectFit={"cover"}
                        w={"80px"}
                        h="90px"
                        borderRadius={"12px"}
                      />

                      <Box>
                        <Heading
                          fontSize={"16px"}
                          color="#4F4F51"
                          mb="9px"
                          fontFamily={"RedHatDisplay-Bold"}
                        >
                          {item.service.name}
                        </Heading>

                        <Text fontSize={"14px"} color="#4F4F51">
                          {item.service.description}
                        </Text>
                      </Box>
                    </Flex>

                    <Box>
                      <Flex justify={"space-between"} align={"center"}>
                        <Flex gap={"10px"}>
                          <Flex align={"center"} gap={"4px"}>
                            <Icon viewBox="0 0 16 16">
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <g clipPath="url(#clip0_700_1180)">
                                  <path
                                    d="M12.6666 9.33342V4.00008C12.6666 3.26675 12.0666 2.66675 11.3333 2.66675H1.99996C1.26663 2.66675 0.666626 3.26675 0.666626 4.00008V9.33342C0.666626 10.0667 1.26663 10.6667 1.99996 10.6667H11.3333C12.0666 10.6667 12.6666 10.0667 12.6666 9.33342ZM6.66663 8.66675C5.55996 8.66675 4.66663 7.77341 4.66663 6.66675C4.66663 5.56008 5.55996 4.66675 6.66663 4.66675C7.77329 4.66675 8.66663 5.56008 8.66663 6.66675C8.66663 7.77341 7.77329 8.66675 6.66663 8.66675ZM15.3333 4.66675V12.0001C15.3333 12.7334 14.7333 13.3334 14 13.3334H2.66663V12.0001H14V4.66675H15.3333Z"
                                    fill="#FF8E3C"
                                    fillOpacity="0.5"
                                  />
                                </g>
                                <defs>
                                  <clipPath id="clip0_700_1180">
                                    <rect width="16" height="16" fill="white" />
                                  </clipPath>
                                </defs>
                              </svg>
                            </Icon>

                            <Text
                              fontSize={"16px"}
                              fontWeight={600}
                              color={"#267684"}
                            >
                              £ {item.finalPrice}
                            </Text>
                          </Flex>

                          <Flex align={"center"} gap={"4px"}>
                            <Icon viewBox="0 0 16 16">
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <g clipPath="url(#clip0_700_1189)">
                                  <path
                                    d="M7.99337 1.33325C4.31337 1.33325 1.33337 4.31992 1.33337 7.99992C1.33337 11.6799 4.31337 14.6666 7.99337 14.6666C11.68 14.6666 14.6667 11.6799 14.6667 7.99992C14.6667 4.31992 11.68 1.33325 7.99337 1.33325ZM8.00004 13.3333C5.05337 13.3333 2.66671 10.9466 2.66671 7.99992C2.66671 5.05325 5.05337 2.66659 8.00004 2.66659C10.9467 2.66659 13.3334 5.05325 13.3334 7.99992C13.3334 10.9466 10.9467 13.3333 8.00004 13.3333Z"
                                    fill="#FF8E3C"
                                    fillOpacity="0.5"
                                  />
                                  <path
                                    d="M8.33337 4.66675H7.33337V8.66675L10.8334 10.7667L11.3334 9.94675L8.33337 8.16675V4.66675Z"
                                    fill="#FF8E3C"
                                    fillOpacity="0.5"
                                  />
                                </g>
                                <defs>
                                  <clipPath id="clip0_700_1189">
                                    <rect width="16" height="16" fill="white" />
                                  </clipPath>
                                </defs>
                              </svg>
                            </Icon>

                            <Text
                              fontSize={"16px"}
                              fontWeight={600}
                              color={"#267684"}
                            >
                              {formatDurationHM(totalDurationMin)}
                            </Text>
                          </Flex>
                        </Flex>

                        <Flex
                          w="40px"
                          h="40px"
                          borderRadius={"20px"}
                          bg="#FFFFFF"
                          boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
                          align={"center"}
                          justify={"center"}
                          cursor={"pointer"}
                          onClick={() =>
                            setCart((prev: any[]) =>
                              prev.filter(
                                (cartedItem: any) =>
                                  cartedItem.service._id !== item.service._id
                              )
                            )
                          }
                        >
                          <Icon fontSize={"24px"} color="#FF8E3C">
                            <PiMinusCircleFill />
                          </Icon>
                        </Flex>
                      </Flex>

                      <Box
                        h={"2px"}
                        mt="10px"
                        bg="#E9EDEF"
                        borderRadius={"10px"}
                      />
                    </Box>
                  </Box>
                );
              })}
          </DrawerBody>

          <DrawerFooter
            boxShadow="0px 5px 10px rgba(0, 0, 0, 0.4)"
            display={"block"}
          >
            <Flex justify={"space-between"} align={"center"}>
              <Text fontSize={"20px"} fontWeight={700} color={"#4F4F51"}>
                Total
              </Text>

              <Text fontSize={"20px"} fontWeight={700} color={"#267684"}>
                £{" "}
                {cart.reduce(
                  (
                    accumulator: any,
                    currentValue: { finalPrice: any; finalDuration: any }
                  ) => accumulator + currentValue.finalPrice,
                  0
                )}
              </Text>
            </Flex>

            <Button
              bg={"#FF8E3C"}
              _hover={{ bg: "#FF8E3C" }}
              mt="24px"
              borderRadius={"30px"}
              color={"white"}
              w="100%"
              h="58px"
              fontSize={"17px"}
              fontFamily={"RedHatDisplay-Bold"}
              onClick={handleSubmit}
            >
              Finalise Booking
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};
